import { type FilterOption, InputFilterMenu } from '@/components/FilterMenu'
import type { DeploymentLabelOption } from '@/contexts/useApiaries'
import { Box, HStack, Icon, Tag, Text, VStack } from '@chakra-ui/react'
import { Check } from 'lucide-react'
import { ActiveFilters, FilterControls } from './components'

interface ClusterConfigFiltersProps {
  selectedOrgId: string | null
  handleOrgIdSearch: (value: string) => void

  selectedDeploymentLabel: DeploymentLabelOption | null
  handleDeploymentLabelChange: (option: DeploymentLabelOption | null) => void

  templatesData: { template_id: string; name?: string }[]
  selectedTemplate: FilterOption | null
  handleTemplateChange: (option: FilterOption | null) => void

  approvalStateOptions: FilterOption[]
  selectedApprovalState: FilterOption | null
  handleApprovalStateChange: (option: FilterOption | null) => void

  hiveStateOptions: FilterOption[]
  selectedHiveState: FilterOption | null
  handleHiveStateChange: (option: FilterOption | null) => void

  selectedHiveId: string | null
  handleHiveIdSearch: (value: string) => void

  tagOptions: FilterOption[]
  selectedTags: FilterOption[]
  setSelectedTags: (tags: FilterOption[]) => void

  sortOrderOptions: FilterOption[]
  selectedSortOption: FilterOption
  handleSortChange: (option: FilterOption | null) => void

  includeDisabled: boolean
  toggleIncludeDisabledChange: () => void

  hasActiveFilters: boolean
  clearAllFilters: () => void

  setPage: (pageNumber: number | ((prev: number) => number)) => void
  copyFilterUrl?: () => void
}

export function ClusterConfigFilters({
  selectedOrgId,
  handleOrgIdSearch,
  selectedDeploymentLabel,
  handleDeploymentLabelChange,
  templatesData,
  selectedTemplate,
  handleTemplateChange,
  approvalStateOptions,
  selectedApprovalState,
  handleApprovalStateChange,
  hiveStateOptions,
  selectedHiveState,
  handleHiveStateChange,
  selectedHiveId,
  handleHiveIdSearch,
  tagOptions,
  selectedTags,
  setSelectedTags,
  sortOrderOptions,
  selectedSortOption,
  handleSortChange,
  includeDisabled,
  toggleIncludeDisabledChange,
  hasActiveFilters,
  clearAllFilters,
  setPage,
  copyFilterUrl,
}: ClusterConfigFiltersProps) {
  // Helper function for rendering tag and value in FilterMenu
  function renderTagAndValue<T>(option: FilterOption<T>, isSelected: boolean) {
    return (
      <HStack>
        {isSelected ? <Icon as={Check} /> : <Box width="16px" />}
        <Tag.Root width="auto">
          <Tag.Label>{option.value as string}</Tag.Label>
        </Tag.Root>
        <Text fontSize="sm">{option.label}</Text>
      </HStack>
    )
  }

  return (
    <VStack
      width="100%"
      gap={4}
      alignItems="start"
      data-testid="filters-and-active-filters"
    >
      {/* Filter Controls Component */}
      <FilterControls
        selectedOrgId={selectedOrgId}
        handleOrgIdSearch={handleOrgIdSearch}
        selectedDeploymentLabel={selectedDeploymentLabel}
        handleDeploymentLabelChange={handleDeploymentLabelChange}
        templatesData={templatesData}
        selectedTemplate={selectedTemplate}
        handleTemplateChange={handleTemplateChange}
        approvalStateOptions={approvalStateOptions}
        selectedApprovalState={selectedApprovalState}
        handleApprovalStateChange={handleApprovalStateChange}
        hiveStateOptions={hiveStateOptions}
        selectedHiveState={selectedHiveState}
        handleHiveStateChange={handleHiveStateChange}
        selectedHiveId={selectedHiveId}
        handleHiveIdSearch={handleHiveIdSearch}
        tagOptions={tagOptions}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        sortOrderOptions={sortOrderOptions}
        selectedSortOption={selectedSortOption}
        handleSortChange={handleSortChange}
        includeDisabled={includeDisabled}
        toggleIncludeDisabledChange={toggleIncludeDisabledChange}
        setPage={setPage}
        hasActiveFilters={hasActiveFilters}
        copyFilterUrl={copyFilterUrl}
        renderTagAndValue={renderTagAndValue}
        InputFilterMenu={InputFilterMenu}
      />

      {/* Display selected filters as closable tags */}
      {hasActiveFilters && (
        <ActiveFilters
          selectedDeploymentLabel={selectedDeploymentLabel}
          handleDeploymentLabelChange={handleDeploymentLabelChange}
          selectedTemplate={selectedTemplate}
          handleTemplateChange={handleTemplateChange}
          selectedApprovalState={selectedApprovalState}
          handleApprovalStateChange={handleApprovalStateChange}
          selectedHiveState={selectedHiveState}
          handleHiveStateChange={handleHiveStateChange}
          selectedHiveId={selectedHiveId}
          handleHiveIdSearch={handleHiveIdSearch}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          selectedOrgId={selectedOrgId}
          handleOrgIdSearch={handleOrgIdSearch}
          setPage={setPage}
          clearAllFilters={clearAllFilters}
        />
      )}
    </VStack>
  )
}
